/**
 * persistence.ts —— localStorage 存档（PRD 5.2 / AC1.9 / AC5.6）
 *
 * 两个必须解决的问题：
 *   1. 引擎的 `SaveSnapshot` 里嵌了多层 `Map`（settlement.payouts、awards[].payouts、
 *      HandRecord.net，以及 events 里的 settle 事件），`JSON.stringify` 默认会把 Map 变成 `{}`
 *      —— 那是典型的「静默丢数据」。这里用 replacer/reviver 显式编解码。
 *   2. 存档除了引擎状态，还要带上 UI 侧不可推导的信息：AI 风格构成、各街赢率快照、
 *      峰值筹码。AI 情绪状态不在引擎契约的序列化范围内，恢复后从 calm 重新累积（见交付说明）。
 */

import { SAVE_SCHEMA_VERSION, type SaveSnapshot } from '../core/game';
import type { StyleKey } from '../core/ai';
import type { EquitySnapshotMap } from './equityTypes';

/** 外层文档版本。引擎存档版本另见 `SaveSnapshot.schemaVersion`。 */
export const DOC_VERSION = 1;
const KEY = 'riverside.save.v1';

export interface PersistedDoc {
  readonly docVersion: number;
  readonly savedAt: number;
  readonly engine: SaveSnapshot;
  readonly styles: Readonly<Record<number, StyleKey>>;
  readonly snapshots: EquitySnapshotMap;
  readonly peakStack: number;
}

export type LoadResult =
  | { readonly status: 'empty' }
  | { readonly status: 'ok'; readonly doc: PersistedDoc }
  | { readonly status: 'incompatible'; readonly reason: string }
  | { readonly status: 'corrupt'; readonly reason: string };

const MAP_TAG = '__map__';

function replacer(_k: string, v: unknown): unknown {
  if (v instanceof Map) return { [MAP_TAG]: [...v.entries()] };
  return v;
}

function reviver(_k: string, v: unknown): unknown {
  if (v && typeof v === 'object' && MAP_TAG in (v as Record<string, unknown>)) {
    return new Map((v as Record<string, [unknown, unknown][]>)[MAP_TAG]);
  }
  return v;
}

export function encodeDoc(doc: PersistedDoc): string {
  return JSON.stringify(doc, replacer);
}

export function decodeDoc(raw: string): PersistedDoc {
  return JSON.parse(raw, reviver) as PersistedDoc;
}

/** 写档。返回耗时（ms），用于核对 PRD 8.1「存档写入 ≤50ms」。 */
export function saveDoc(doc: PersistedDoc): { ok: boolean; elapsedMs: number; error?: string } {
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  try {
    if (typeof localStorage === 'undefined') return { ok: false, elapsedMs: 0, error: 'localStorage 不可用' };
    localStorage.setItem(KEY, encodeDoc(doc));
    const t1 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return { ok: true, elapsedMs: t1 - t0 };
  } catch (e) {
    const t1 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return { ok: false, elapsedMs: t1 - t0, error: e instanceof Error ? e.message : String(e) };
  }
}

export function loadDoc(): LoadResult {
  if (typeof localStorage === 'undefined') return { status: 'empty' };
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return { status: 'empty' };
  }
  if (!raw) return { status: 'empty' };
  let doc: PersistedDoc;
  try {
    doc = decodeDoc(raw);
  } catch (e) {
    return { status: 'corrupt', reason: e instanceof Error ? e.message : String(e) };
  }
  if (!doc || typeof doc !== 'object' || !doc.engine) {
    return { status: 'corrupt', reason: '存档结构不完整' };
  }
  if (doc.docVersion !== DOC_VERSION) {
    return { status: 'incompatible', reason: `存档文档版本 v${doc.docVersion}，当前应用为 v${DOC_VERSION}` };
  }
  if (doc.engine.schemaVersion !== SAVE_SCHEMA_VERSION) {
    return {
      status: 'incompatible',
      reason: `存档引擎版本 v${doc.engine.schemaVersion}，当前引擎为 v${SAVE_SCHEMA_VERSION}`,
    };
  }
  return { status: 'ok', doc };
}

export function clearDoc(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 忽略 */
  }
}

/** 存档摘要（启动页「继续上次对局」卡片用）。 */
export interface SaveSummary {
  readonly handNumber: number;
  readonly seatCount: number;
  readonly smallBlind: number;
  readonly bigBlind: number;
  readonly myStack: number;
  readonly savedAt: number;
  readonly phase: string;
}

export function summarize(doc: PersistedDoc): SaveSummary {
  const me = doc.engine.players.find((p) => p.isHuman);
  return {
    handNumber: doc.engine.handNumber,
    seatCount: doc.engine.config.seatCount,
    smallBlind: doc.engine.config.smallBlind,
    bigBlind: doc.engine.config.bigBlind,
    myStack: me?.stack ?? 0,
    savedAt: doc.savedAt,
    phase: doc.engine.phase,
  };
}
